// react
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';

// components
import { Icon, Icons } from '@/components/atoms/Icon';

// theme
import { theme } from '@/theme';
import { Color } from '@/theme/color';

// models
import { ActionPlanCategory } from '@karehero/models';

interface ActionPlanPathwayProps {
  className?: string;
  actionPlanCategories: ActionPlanCategory[];
}

/**
 * ActionPlanPathway displays a pathway of action plan categories.
 */
export const ActionPlanPathway = ({
  className,
  actionPlanCategories,
}: ActionPlanPathwayProps) => {
  return (
    <Wrapper className={className}>
      {actionPlanCategories?.map((category, i) => (
        <CategoryWrapper $isFlip={i % 2 == 1}>
          <IconButton to={`/action-plan/category/${category.id}`}>
            <Icon
              icon={category.icon as Icons}
              fill={
                theme.color.actionPlanCards[category.color].secondary as Color
              }
              size='3x'
              isElevation
            />
            <Info>
              <Title>{category.title}</Title>
              {category.completedItems !== undefined &&
                category.totalItems !== undefined && (
                  <Progress>
                    {category.completedItems}/{category.totalItems}
                  </Progress>
                )}
            </Info>
          </IconButton>
          {i !== actionPlanCategories.length - 1 ? (
            <Connector $isFlip={i % 2 == 1} />
          ) : (
            <div />
          )}
        </CategoryWrapper>
      ))}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${(props) => props.theme.spacing[32]};
  padding-top: 0;
  padding-bottom: ${(props) => props.theme.spacing[64]};
`;

interface CategoryWrapperProps {
  $isFlip: boolean;
}
const CategoryWrapper = styled.div<CategoryWrapperProps>`
  display: flex;
  align-items: flex-start;
  height: min-content;
  flex-direction: ${(props) => (props.$isFlip ? 'row-reverse' : 'row')};
  padding-${(props) => (props.$isFlip ? 'left' : 'right')}: 50px;
`;

const IconButton = styled(Link)`
  all: unset;
  position: relative;
  transition-duration: 0.2s;
  &:hover {
    cursor: pointer;
    transform: scale(1.1);
  }
`;

interface ConnectorProps {
  $isFlip?: boolean;
}
const Connector = styled.div<ConnectorProps>`
  background: transparent;
  border-top: 2px dashed ${theme.color.neutral[70]};
  ${(props) =>
    props.$isFlip
      ? css`
          border-left: 2px dashed ${theme.color.neutral[70]};
          border-top-left-radius: 32px;
        `
      : css`
          border-right: 2px dashed ${theme.color.neutral[70]};
          border-top-right-radius: 32px;
        `}
  min-height: calc(50px + 25px);
  margin-top: 50px;
  width: 100%;
`;

const Info = styled.div`
  position: absolute;
  left: 50%;
  bottom: -${(props) => props.theme.spacing[4]};
  transform: translate(-50%, 100%);
  display: flex;
  flex-direction: column;
  white-space: nowrap;
`;

const Title = styled.div`
  text-align: center;
  font: ${(props) => props.theme.typography.body.lg.medium};
  color: ${(props) => props.theme.color.neutral[100]};
`;

const Progress = styled.div`
  text-align: center;
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[90]};
`;

ActionPlanPathway.displayName = 'ActionPlanPathway';

export default ActionPlanPathway;
